import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiPlus, FiTrash2, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import styles from '../Lead.module.css';
import useEsc from '../../../../hooks/useEsc';
import { FLUXOS } from '../fluxos';
import {
  listarItensChecklist, criarItemChecklist, atualizarItemChecklist, excluirItemChecklist,
} from '../../../../services/leadDocumentos';
import {
  listarModelosContrato, criarModeloContrato, atualizarModeloContrato, excluirModeloContrato,
  listarMarcadores, buscarContratada, salvarContratada, TIPOS_MODELO,
} from '../../../../services/leadContratos';
import TextoMinuta from './TextoMinuta';

/* Cadastro do checklist de documentos por fluxo. Só gerente e
   administrador editam (o backend confere); os demais só veem. */
function AbaChecklist() {
  const [itens, setItens] = useState([]);
  const [podeEditar, setPodeEditar] = useState(false);
  const [fluxo, setFluxo] = useState(FLUXOS[0].id);
  const [novo, setNovo] = useState('');
  const [erro, setErro] = useState('');

  const carregar = useCallback(() => listarItensChecklist()
    .then(r => { setItens(r.items || []); setPodeEditar(!!r.podeEditar); })
    .catch(e => setErro(e.message)), []);
  useEffect(() => { carregar(); }, [carregar]);

  const doFluxo = itens.filter(i => i.fluxo === fluxo).sort((a, b) => a.ordem - b.ordem);

  async function acao(fn) {
    setErro('');
    try { await fn(); await carregar(); } catch (e) { setErro(e.message); }
  }
  const adicionar = () => novo.trim() && acao(async () => { await criarItemChecklist(fluxo, novo.trim()); setNovo(''); });
  /* troca a ordem com o vizinho (as ordens podem repetir: renumera o fluxo todo) */
  const mover = (idx, delta) => acao(async () => {
    const lista = [...doFluxo];
    const [x] = lista.splice(idx, 1);
    lista.splice(idx + delta, 0, x);
    await Promise.all(lista.map((it, i) => (it.ordem !== i ? atualizarItemChecklist(it.id, { ordem: i }) : null)));
  });

  return (
    <div className={styles.configCorpo}>
      <div className={styles.tiposGrid}>
        {FLUXOS.map(f => (
          <button key={f.id} type="button" className={`${styles.tipoBadge} ${fluxo === f.id ? styles.tipoBadgeAtivo : ''}`}
            onClick={() => setFluxo(f.id)}>{f.curto} ({itens.filter(i => i.fluxo === f.id && i.ativo).length})</button>
        ))}
      </div>
      {!podeEditar && <p className={styles.leadSecaoSub}>Só gerente e administrador alteram o checklist.</p>}
      {erro && <p className={styles.avisoReceitaErro}>{erro}</p>}
      <div className={styles.configLista}>
        {doFluxo.map((it, idx) => (
          <div key={it.id} className={`${styles.configItem} ${!it.ativo ? styles.configItemInativo : ''}`}>
            <input className={styles.input} defaultValue={it.nome} disabled={!podeEditar}
              onBlur={e => e.target.value.trim() && e.target.value.trim() !== it.nome && acao(() => atualizarItemChecklist(it.id, { nome: e.target.value }))} />
            {podeEditar && (
              <>
                <label className={styles.configAtivo} title="Item inativo não entra no checklist de novos leads">
                  <input type="checkbox" checked={it.ativo} onChange={e => acao(() => atualizarItemChecklist(it.id, { ativo: e.target.checked }))} /> ativo
                </label>
                <button type="button" className={styles.configBtnIcone} disabled={idx === 0} onClick={() => mover(idx, -1)} title="Subir"><FiArrowUp size={13} /></button>
                <button type="button" className={styles.configBtnIcone} disabled={idx === doFluxo.length - 1} onClick={() => mover(idx, 1)} title="Descer"><FiArrowDown size={13} /></button>
                <button type="button" className={styles.configBtnIcone} title="Excluir"
                  onClick={() => window.confirm(`Excluir "${it.nome}"? Nos leads que já têm esse item ele continua com o nome atual.`) && acao(() => excluirItemChecklist(it.id))}>
                  <FiTrash2 size={13} />
                </button>
              </>
            )}
          </div>
        ))}
        {!doFluxo.length && <p className={styles.leadSecaoSub}>Nenhum item neste fluxo.</p>}
      </div>
      {podeEditar && (
        <div className={styles.configNovo}>
          <input className={styles.input} placeholder="Novo documento deste fluxo" value={novo}
            onChange={e => setNovo(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), adicionar())} />
          <button type="button" className={styles.btnAdicionarObrigacao} onClick={adicionar}><FiPlus size={15} /> Adicionar</button>
        </div>
      )}
    </div>
  );
}

/* Modelos de contrato: lista à esquerda, editor + marcadores + prévia */
function AbaModelos() {
  const [modelos, setModelos] = useState([]);
  const [podeEditar, setPodeEditar] = useState(false);
  const [marcadores, setMarcadores] = useState([]);
  const [selId, setSelId] = useState(null);
  const [form, setForm] = useState(null);
  const [verPrevia, setVerPrevia] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const textoRef = React.useRef(null);

  const carregar = useCallback(() => listarModelosContrato().then(r => {
    setModelos(r.items || []); setPodeEditar(!!r.podeEditar);
    return r.items || [];
  }).catch(e => setErro(e.message)), []);
  useEffect(() => { carregar().then(l => l && l[0] && abrir(l[0])); listarMarcadores().then(setMarcadores).catch(() => {}); }, [carregar]); // eslint-disable-line react-hooks/exhaustive-deps

  function abrir(m) { setSelId(m?.id || null); setForm(m ? { nome: m.nome, tipo: m.tipo, status: m.status, conteudo: m.conteudo } : { nome: '', tipo: 'outro', status: 'rascunho_revisar', conteudo: '' }); setMsg(''); setErro(''); }
  async function salvar() {
    setErro(''); setMsg('');
    try {
      const r = selId ? await atualizarModeloContrato(selId, form) : await criarModeloContrato(form);
      await carregar(); setSelId(r.id); setMsg('Modelo salvo.');
    } catch (e) { setErro(e.message); }
  }
  async function excluir() {
    if (!selId || !window.confirm(`Excluir o modelo "${form.nome}"? As minutas já geradas não mudam.`)) return;
    try { await excluirModeloContrato(selId); const l = await carregar(); abrir(l?.[0]); } catch (e) { setErro(e.message); }
  }
  /* insere o marcador onde está o cursor */
  function inserir(campo) {
    const el = textoRef.current;
    const marca = `{{${campo}}}`;
    if (!el) return;
    const ini = el.selectionStart ?? form.conteudo.length;
    const fim = el.selectionEnd ?? ini;
    const novo = form.conteudo.slice(0, ini) + marca + form.conteudo.slice(fim);
    setForm(f => ({ ...f, conteudo: novo }));
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = ini + marca.length; }, 0);
  }

  return (
    <div className={styles.modelosGrade}>
      <div className={styles.modelosLista}>
        {modelos.map(m => (
          <button key={m.id} type="button" className={`${styles.modeloItem} ${selId === m.id ? styles.modeloItemAtivo : ''}`} onClick={() => abrir(m)}>
            <strong>{m.nome}</strong>
            <span>{TIPOS_MODELO.find(t => t.id === m.tipo)?.label || m.tipo}</span>
            <em className={m.status === 'liberado' ? styles.seloLiberado : styles.seloRevisar}>{m.status === 'liberado' ? 'liberado' : 'rascunho – revisar'}</em>
          </button>
        ))}
        {podeEditar && <button type="button" className={styles.btnConfig} onClick={() => abrir(null)}><FiPlus size={13} /> Novo modelo</button>}
      </div>
      {form && (
        <div className={styles.modeloEditor}>
          {!podeEditar && <p className={styles.leadSecaoSub}>Só gerente e administrador alteram os modelos.</p>}
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Nome</label>
              <input className={styles.input} value={form.nome} disabled={!podeEditar} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Tipo</label>
              <select className={styles.select} value={form.tipo} disabled={!podeEditar} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                {TIPOS_MODELO.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Status</label>
              <select className={styles.select} value={form.status} disabled={!podeEditar} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="rascunho_revisar">Rascunho – revisar</option>
                <option value="liberado">Liberado para uso (revisado)</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>&nbsp;</label>
              <label className={styles.configAtivo}><input type="checkbox" checked={verPrevia} onChange={e => setVerPrevia(e.target.checked)} /> ver prévia</label>
            </div>
          </div>
          <p className={styles.leadSecaoSub} style={{ margin: 0 }}>
            Marcação: <code># título</code> · <code>## cláusula</code> · <code>- item</code> · <code>**negrito**</code> · linha em branco separa parágrafo. Clique num marcador para inserir.
          </p>
          <div className={styles.modeloTextoGrade}>
            {verPrevia
              ? <div className={styles.minutaColuna}><TextoMinuta texto={form.conteudo} /></div>
              : <textarea ref={textoRef} className={styles.minutaTexto} value={form.conteudo} disabled={!podeEditar}
                  onChange={e => setForm(f => ({ ...f, conteudo: e.target.value }))} spellCheck />}
            <div className={styles.marcadoresLista}>
              {marcadores.map(mk => (
                <button key={mk.campo} type="button" className={styles.marcadorBtn} title={mk.descricao}
                  disabled={!podeEditar || verPrevia} onClick={() => inserir(mk.campo)}>
                  <code>{`{{${mk.campo}}}`}</code><span>{mk.descricao}</span>
                </button>
              ))}
            </div>
          </div>
          {erro && <p className={styles.avisoReceitaErro}>{erro}</p>}
          {msg && <p className={styles.avisoReceita}>{msg}</p>}
          {podeEditar && (
            <div className={styles.configNovo} style={{ justifyContent: 'flex-end' }}>
              {selId && <button type="button" className={styles.btnCancelar} onClick={excluir}><FiTrash2 size={13} /> Excluir</button>}
              <button type="button" className={styles.btnSalvar} onClick={salvar}>Salvar modelo</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* Dados da Essensials Partners que entram no contrato */
function AbaContratada() {
  const [d, setD] = useState(null);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => { buscarContratada().then(setD).catch(e => setErro(e.message)); }, []);
  if (!d) return erro ? <p className={styles.avisoReceitaErro}>{erro}</p> : <p>Carregando...</p>;
  const campo = (c, rotulo, ph = '') => (
    <div className={styles.formGroup}>
      <label className={styles.label}>{rotulo}</label>
      <input className={styles.input} value={d[c] || ''} placeholder={ph} disabled={!d.podeEditar} onChange={e => setD(x => ({ ...x, [c]: e.target.value }))} />
    </div>
  );
  async function salvar() {
    setErro(''); setMsg('');
    try { const r = await salvarContratada(d); setD(x => ({ ...x, ...r })); setMsg('Dados salvos. Valem para as próximas minutas geradas.'); } catch (e) { setErro(e.message); }
  }
  return (
    <div className={styles.configCorpo}>
      <p className={styles.leadSecaoSub} style={{ margin: 0 }}>Entram nos marcadores {'{{contratada_...}}'} e {'{{foro}}'} dos modelos.</p>
      {!d.podeEditar && <p className={styles.leadSecaoSub}>Só gerente e administrador alteram estes dados.</p>}
      <div className={styles.formGrid}>
        {campo('razao_social', 'Razão social')}
        {campo('cnpj', 'CNPJ')}
        <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
          <label className={styles.label}>Endereço completo</label>
          <input className={styles.input} value={d.endereco || ''} disabled={!d.podeEditar} placeholder="Rua, número, bairro, cidade/UF, CEP"
            onChange={e => setD(x => ({ ...x, endereco: e.target.value }))} />
        </div>
        {campo('representante_nome', 'Representante legal')}
        {campo('representante_cpf', 'CPF do representante')}
        {campo('cidade_foro', 'Cidade do foro', 'Ex.: Tatuí/SP')}
      </div>
      {erro && <p className={styles.avisoReceitaErro}>{erro}</p>}
      {msg && <p className={styles.avisoReceita}>{msg}</p>}
      {d.podeEditar && <div><button type="button" className={styles.btnSalvar} onClick={salvar}>Salvar</button></div>}
    </div>
  );
}

export const ABAS_CONFIG = [
  { id: 'modelos', label: 'Modelos de contrato', Componente: AbaModelos },
  { id: 'checklist', label: 'Checklist de documentos', Componente: AbaChecklist },
  { id: 'contratada', label: 'Dados da contratada', Componente: AbaContratada },
];

/* Modal "Modelos e checklist" da página de Leads */
function ConfigLeads({ onFechar, abas = ABAS_CONFIG }) {
  const [aba, setAba] = useState(abas[0].id);
  useEsc(onFechar);
  const Atual = abas.find(a => a.id === aba)?.Componente;
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitulo}>Modelos e checklist</h2>
            <p className={styles.modalSubtitulo}>Configuração dos leads da Essensials Partners</p>
          </div>
          <button type="button" className={styles.modalBtnFechar} onClick={onFechar}><FiX size={20} /></button>
        </div>
        <div className={styles.tabNav}>
          {abas.map((a, i) => (
            <button key={a.id} type="button" className={`${styles.tabBtn} ${a.id === aba ? styles.tabBtnAtivo : ''}`} onClick={() => setAba(a.id)}>
              <span className={styles.tabNumero}>{i + 1}</span>
              <span className={styles.tabLabel}>{a.label}</span>
            </button>
          ))}
        </div>
        <div className={styles.modalBody}>{Atual && <Atual />}</div>
      </div>
    </div>
  );
}

export default ConfigLeads;
